import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import LoginPage       from "./Login";
import PageLoader      from "./layout/PageLoader";
import { logAudit, AUDIT_ACTION, AUDIT_MODULE } from "./utils/auditLogger";

// Everything that only matters post-login (providers, Sidebar/Header, the
// lazy-loaded pages) lives in AppShell.jsx and loads as its own chunk — see
// that file's header comment for why.
const LoggedInApp = lazy(() => import("./AppShell"));

// PreRegisterPage imports Firestore directly (it writes a pre-registration
// doc) — lazy-load it too, so the rarely-hit /pre-register public route
// doesn't pull Firestore into the bundle every other visitor downloads.
const PreRegisterPage = lazy(() => import("./pages/PreRegisterPage"));

// ── Device detection helper ───────────────────────────────────
function detectDevice() {
  const w = window.innerWidth;
  if (w < 768)  return "Mobile";
  if (w < 1024) return "Tablet";
  return "Desktop";
}

// ── Session ID helper — persists per browser tab session ──────
function getSessionId() {
  let sid = sessionStorage.getItem("saksham_session_id");
  if (!sid) {
    // crypto.randomUUID() requires HTTPS — fallback for HTTP/older browsers
    sid = typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : "xxxx-xxxx-xxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
    sessionStorage.setItem("saksham_session_id", sid);
  }
  return sid;
}

export default function App() {
  const [user,        setUser]        = useState(null);
  const [activeNav,   setActiveNav]   = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile,    setIsMobile]    = useState(window.innerWidth < 768);
  const [isTablet,    setIsTablet]    = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const presenceRef = useRef(false);

  // Reactive responsive detection — updates on window resize / rotation
  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── STEP 3a — Presence tracking on login ────────────────────
  // Firestore is lazy-imported here (rather than at module top level) so
  // App.jsx — which renders before any login happens — never pulls the
  // Firestore SDK into the initial bundle.
  useEffect(() => {
    if (!user || presenceRef.current) return;
    presenceRef.current = true;

    const sessionId   = getSessionId();
    const device      = detectDevice();
    const displayName = user.name || user.username || "Unknown";
    const role        = user.role || "volunteer";

    let cancelled  = false;
    let heartbeat  = null;
    let handleUnload = null;
    let presDocRef = null;
    let setDocFn = null;
    let serverTimestampFn = null;

    (async () => {
      const [{ db }, { doc, setDoc, serverTimestamp }] = await Promise.all([
        import("./firebase"),
        import("firebase/firestore"),
      ]);
      if (cancelled) return; // effect was cleaned up before this resolved

      setDocFn = setDoc;
      serverTimestampFn = serverTimestamp;
      presDocRef = doc(db, "userPresence", user.uid);

      // Write userPresence document
      setDoc(presDocRef, {
        userId:      user.uid,
        displayName,
        role,
        lastSeen:    serverTimestamp(),
        isOnline:    true,
        device,
        sessionId,
        category:    "system",
      }, { merge: true }).catch(err =>
        console.warn("[Presence] Write failed:", err?.message)
      );

      // STEP 3c — Audit: USER_ONLINE
      logAudit({
        user,
        action:  AUDIT_ACTION.USER_ONLINE,
        module:  AUDIT_MODULE.PRESENCE,
        notes:   `Session: ${sessionId.slice(0, 8)}, Device: ${device}`,
      });

      // STEP 3b — App hit (only if new session, not existing)
      const hitAlreadyLogged = sessionStorage.getItem("saksham_hit_logged");
      if (!hitAlreadyLogged) {
        const hitDocRef = doc(db, "appHits", sessionId);
        setDoc(hitDocRef, {
          userId:      user.uid,
          displayName,
          role,
          timestamp:   serverTimestamp(),
          device,
          sessionId,
          category:    "system",
        }).then(() => {
          sessionStorage.setItem("saksham_hit_logged", "true");
        }).catch(err =>
          console.warn("[AppHits] Write failed:", err?.message)
        );

        // STEP 3d — Audit: APP_HIT
        logAudit({
          user,
          action:  AUDIT_ACTION.APP_HIT,
          module:  AUDIT_MODULE.PRESENCE,
          notes:   `New session hit — Device: ${device}`,
        });
      }

      // STEP 4 — Heartbeat: update lastSeen + isOnline every 5 minutes
      heartbeat = setInterval(() => {
        setDoc(presDocRef, {
          lastSeen: serverTimestamp(),
          isOnline: true,
        }, { merge: true }).catch(() => {});
      }, 5 * 60 * 1000);

      // STEP 6 — Go offline on browser tab close
      handleUnload = () => {
        // navigator.sendBeacon is most reliable during beforeunload
        const payload = JSON.stringify({
          fields: {
            isOnline: { booleanValue: false },
          },
        });
        const url = `https://firestore.googleapis.com/v1/projects/saksham-tn/databases/(default)/documents/userPresence/${user.uid}?updateMask.fieldPaths=isOnline`;
        const sent = navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
        // Fallback: best-effort setDoc
        if (!sent) {
          setDoc(presDocRef, { isOnline: false, lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
        }
      };
      window.addEventListener("beforeunload", handleUnload);
    })();

    // STEP 5 — Cleanup on unmount (logout / nav away)
    return () => {
      cancelled = true;
      if (heartbeat) clearInterval(heartbeat);
      if (handleUnload) window.removeEventListener("beforeunload", handleUnload);

      // Set offline (only if the async init above actually completed)
      if (presDocRef && setDocFn && serverTimestampFn) {
        setDocFn(presDocRef, {
          isOnline: false,
          lastSeen: serverTimestampFn(),
        }, { merge: true }).catch(() => {});
      }

      // Audit: USER_OFFLINE
      logAudit({
        user,
        action:  AUDIT_ACTION.USER_OFFLINE,
        module:  AUDIT_MODULE.PRESENCE,
        notes:   `Session: ${sessionId.slice(0, 8)}`,
      });

      presenceRef.current = false;
    };
  }, [user]);

  // Public route — render without auth check
  if (window.location.pathname.startsWith("/pre-register")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <PreRegisterPage />
      </Suspense>
    );
  }

  if (!user) return <LoginPage onLogin={u => setUser(u)} />;

  return (
    <Suspense fallback={<PageLoader />}>
      <LoggedInApp
        user={user}
        activeNav={activeNav}
        setActiveNav={setActiveNav}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isMobile={isMobile}
        isTablet={isTablet}
        setUser={setUser}
      />
    </Suspense>
  );
}
